import { useCallback } from 'react';
import axios, { useApiRequest } from './axios';
import { buildFilterParams } from './filterParams';
import { CommonListResponse } from '../types/crud';

export const useCRUD = (endpoint: string) => {
  const { loading, error, executeRequest } = useApiRequest();

  const create = useCallback(async (data: any): Promise<any | undefined> => {
    return await executeRequest(() => axios.post(endpoint, { ...data }));
  }, [executeRequest, endpoint]);

  const query = useCallback(async (queryOptions: any): Promise<CommonListResponse<any> | undefined> => {
    const params: any = {
      page: queryOptions.page + 1,
      page_size: queryOptions.pageSize,
      ordering: queryOptions.sortModel?.length
        ? queryOptions.sortModel
          .map((s: any) => (s.sort === 'desc' ? `-${s.field}` : s.field))
          .join(',')
        : undefined,
      search: queryOptions.filterModel?.quickFilterValues?.length
        ? queryOptions.filterModel.quickFilterValues.join(' ')
        : undefined,
    };

    // Map DataGrid filterModel.items to query params, operator-aware (see filterParams.ts)
    try {
      Object.assign(params, buildFilterParams(queryOptions.filterModel?.items));
    } catch (e) {
      // ignore mapping errors
    }
    // Static scope params (e.g. technician_id) always win over same-named
    // column filters - they come from the caller, not from user-editable
    // GridFilterModel state.
    Object.assign(params, queryOptions.extraParams);
    const { data } = await executeRequest(() => axios.get(endpoint, { params }));
    return data;
  }, [executeRequest, endpoint]);

  const get = useCallback(async (id: string | number): Promise<any | undefined> => {
    const { data, success, errorData } = await executeRequest(() => axios.get(`${endpoint}${id}/`));
    return { data, success, errorData };
  }, [executeRequest, endpoint]);

  const update = useCallback(async (data: any): Promise<any | undefined> => {
    const { id, ...rest } = data;
    const { data: responseData, success, errorData } = await executeRequest(() => axios.patch(`${endpoint}${id}/`, { ...rest }));
    return { data: responseData, success, errorData };
  }, [executeRequest, endpoint]);

  /** Full update with PUT so the backend validates all required fields (no partial). */
  const updatePUT = useCallback(async (data: any): Promise<any | undefined> => {
    const { id, ...rest } = data;
    const { data: responseData, success, errorData } = await executeRequest(() => axios.put(`${endpoint}${id}/`, { ...rest }));
    return { data: responseData, success, errorData };
  }, [executeRequest, endpoint]);

  const options = useCallback(async (): Promise<any | undefined> => {
    return await executeRequest(() => axios.options(endpoint));
  }, [executeRequest, endpoint]);

  const remove = useCallback(async (data: any): Promise<any | undefined> => {
    const { id, params, ...rest } = data;
    const { success, errorData } = await executeRequest(() =>
      axios.delete(`${endpoint}${id}/`, { params, ...rest })
    );
    return { success, errorData };
  }, [executeRequest, endpoint]);

  return { loading, error, options, create, query, get, update, updatePUT, remove };
};
